// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";

import StepSettings from "./step-settings";
import StepLevels from "./step-levels";
import StepCompliance from "./step-compliance";
import AbsenceMonitoringActions from "./absence-monitoring-actions";
import StepHierarchyScope from "./step-hierarchy-scope";

type OwnProps = $Exact<{
  clientId: number,
  onCancel: () => void,
  schema: Object,
}>;

type PropsFromState = $Exact<{
  draft: ?AbsenceMonitoring,
  assignments: Array<HierarchyAssignment>,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  add: typeof actionCreators.AddAbsenceMonitoring,
  resetState: typeof actionCreators.ResetAbsenceMonitoringState,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class AbsenceMonitoringAddModal extends React.Component<Props> {
  validate: *;

  get isValid() {
    this.validate = new Ajv().compile(this.schema);

    if (!this.validate || !this.props.draft) {
      return false;
    }

    const draft = removeNullAndUndefinedProps(this.props.draft?.content || {});
    let isValid = this.validate(draft);

    if (!isValid) {
      console.warn("AbsenceMonitoringAddModal validation error: ", this.validate.errors);
    }

    const validSupportAction = (draft.additionalSupportActions || []).find(el => el.isActive);
    const validReasons = (draft.noActionReasons || []).find(el => el.isActive);
    if (!validSupportAction || !validReasons) {
      return false;
    }

    return isValid;
  }

  handleSubmit = () => {
    this.props.add({
      clientId: this.props.clientId,
      draft: this.props.draft?.content,
      assignments: this.props.assignments,
      isActive: this.props.isActive,
    });
    this.props.onCancel();
    this.props.resetState();
  };

  get schema() {
    return this.props.schema;
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onCancel}>
          {this.props.t("ABSENCE_MONITORING_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <LinearWizard defaultStep={1} data={[]}>
            <Step component={StepSettings} title={this.props.t("ABSENCE_MONITORING_STEP1")} />
            <Step
              component={StepCompliance}
              title={this.props.t("ABSENCE_MONITORING_STEP2_COMPLIANCE")}
            />
            <Step component={StepLevels} title={this.props.t("ABSENCE_MONITORING_STEP3")} />
            <Step component={AbsenceMonitoringActions} title={this.props.t("ACTION_LISTS")} />
            <Step component={StepHierarchyScope} title={this.props.t("ABSENCE_MONITORING_STEP5")} />
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            disabled={!(this.isValid && this.props.isAssignmentsValid)}
            onClick={this.handleSubmit}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    draft: serviceSelectors.getAbsenceMonitoringDraft(state),
    assignments: serviceSelectors.getAbsenceMonitoringAssignments(state),
    isAssignmentsValid: serviceSelectors.isAbsenceMonitoringAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  add: actionCreators.AddAbsenceMonitoring,
  resetState: actionCreators.ResetAbsenceMonitoringState,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringAddModal);
